import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Rocket, Users, Sparkles, BookOpen, FolderDown, Megaphone,
  ArrowRight, Check, TrendingUp, ShieldCheck, Zap, Crown, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1544531586-fde5298cdd40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGxlYWRlciUyMHN0YWdlJTIwYXVkaWVuY2V8ZW58MHx8fHwxNzgxNjM5MTcwfDA&ixlib=rb-4.1.0&q=85";
const BOOKS_IMG =
  "https://images.unsplash.com/photo-1535905557558-afc4877a26fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxkYXJrJTIwbGlicmFyeSUyMGJvb2tzfGVufDB8fHx8MTc4MTYzOTE4MHww&ixlib=rb-4.1.0&q=85";
const SYS_IMG =
  "https://images.unsplash.com/photo-1508780709619-79562169bc64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB1c2luZyUyMGxhcHRvcCUyMGRhcmt8ZW58MHx8fHwxNzgxNjM5MTcwfDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

const Brand = () => (
  <span className="font-display font-black tracking-tight">
    MULTI<span className="text-purple-500">CONTACTOS</span>
  </span>
);

export default function Landing() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-[#050505] text-white overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="text-lg sm:text-xl"><Brand /></Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="text-sm text-zinc-300 hover:text-white transition-colors hidden sm:block">
              Iniciar sesión
            </Link>
            <Button
              data-testid="nav-cta"
              onClick={() => navigate("/login")}
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-5"
            >
              Empezar ahora
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Líder de multinivel" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="hidden" animate="show" className="lg:col-span-8">
            <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-purple-300 border border-purple-500/40 rounded-full px-4 py-1.5 mb-7">
              <Crown size={14} className="text-yellow-400" /> Para líderes de multinivel
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="font-display font-black tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
              CONVIÉRTETE EN EL <span className="text-purple-500">LÍDER DE MULTINIVEL</span> QUE <span className="text-yellow-impact">MERECES</span> SER
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-7 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
              La diferencia entre los grandes líderes que ganan millones y quienes nunca alcanzan la libertad financiera
              está en una sola cosa: los <span className="text-white font-semibold">CONTACTOS</span>. MULTICONTACTOS te
              entrega herramientas especializadas de la industria y un sistema de marketing digital automático para hacer
              crecer tu red, aunque no sepas nada de tecnología ni de marketing.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-cta-button"
                onClick={() => navigate("/login")}
                className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-base rounded-full px-8 h-13 py-6 group"
              >
                Quiero ser líder <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                data-testid="hero-secondary"
                variant="outline"
                onClick={() => document.getElementById("areas")?.scrollIntoView({ behavior: "smooth" })}
                className="border-purple-500/50 bg-transparent hover:bg-purple-600 text-white rounded-full px-8 py-6 h-13"
              >
                Ver herramientas
              </Button>
            </motion.div>
            <motion.div variants={fadeUp} custom={4} className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-zinc-400">
              {["Campañas completas con imágenes y videos", "Publica en WhatsApp, Facebook, Instagram y TikTok", "Prospectos calificados a tu WhatsApp"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check size={16} className="text-purple-500" /> {t}</span>
              ))}
            </motion.div>
          </motion.div>
        </div>
        {/* Acceso admin (abajo a la izquierda del hero) */}
        <div className="absolute bottom-5 left-5 sm:left-8 z-20">
          <Link
            to="/admin"
            data-testid="hero-admin-access"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            <Lock size={12} /> Acceso administrador
          </Link>
        </div>
      </header>

      {/* Stats */}
      <section className="border-y border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Users, n: "Tu red", l: "crece en automático" },
            { icon: Zap, n: "IA 24/7", l: "especialista en MLM" },
            { icon: TrendingUp, n: "+ Prospectos", l: "altamente calificados" },
            { icon: ShieldCheck, n: "Sin spam", l: "métodos éticos" },
          ].map((s, i) => (
            <div key={s.n} className="text-center sm:text-left">
              <s.icon className="mx-auto sm:mx-0 text-purple-500 mb-3" size={26} />
              <div className="font-display font-black text-2xl">{s.n}</div>
              <div className="text-sm text-zinc-500">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Areas */}
      <section id="areas" className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-zinc-500 mb-4">Tu módulo de líder</p>
          <h2 className="font-display font-black tracking-tight text-3xl sm:text-5xl">
            Todo lo que la industria usa, <span className="text-purple-500">en un solo lugar</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5 rounded-2xl overflow-hidden border border-white/10 group relative min-h-[320px]">
            <img src={BOOKS_IMG} alt="Área de libros" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="relative p-8 h-full flex flex-col justify-end">
              <BookOpen className="text-yellow-400 mb-3" size={28} />
              <h3 className="font-display font-bold text-2xl">Área de Libros</h3>
              <p className="text-zinc-300 mt-2 text-sm">Biblioteca de clásicos libres de derechos para formar tu mentalidad de líder.</p>
            </div>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
            {[
              { icon: FolderDown, t: "Área de Archivos", d: "Recursos descargables, enlaces a páginas clave de la industria y consejos accionables." },
              { icon: Megaphone, t: "Área de Marketing", d: "Sistema híbrido para crear y publicar tu marketing digital sin saber de tecnología." },
              { icon: Sparkles, t: "IA Especialista MLM", d: "Genera conceptos, mensajes e imágenes diseñados para reclutar distribuidores." },
              { icon: Rocket, t: "Embudos Automáticos", d: "Páginas de captura que califican prospectos y los envían a tu WhatsApp." },
            ].map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-[#121212] p-7 hover:border-purple-500/60 hover:-translate-y-1 transition-all"
              >
                <c.icon className="text-purple-500 mb-4" size={26} />
                <h3 className="font-display font-bold text-lg">{c.t}</h3>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl overflow-hidden border border-white/10 glow-purple">
            <img src={SYS_IMG} alt="Sistema digital" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-zinc-500 mb-4">Marketing en automático</p>
            <h2 className="font-display font-black tracking-tight text-3xl sm:text-5xl mb-8">
              De una idea a <span className="text-yellow-impact">prospectos calificados</span>
            </h2>
            <div className="space-y-5">
              {[
                "Escribe una idea simple. La IA crea una estrategia de alto impacto.",
                "Guarda tu empresa, productos y plan de compensación.",
                "Genera campañas completas: mensaje, imágenes y video listos para publicar.",
                "Publica en WhatsApp, Facebook, Instagram y TikTok (cuentas personales o Meta Business Suite).",
                "Tus embudos califican prospectos con IA y te los envían por WhatsApp.",
              ].map((step, i) => (
                <div key={step} className="flex gap-4">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center font-display font-bold text-sm">{i + 1}</div>
                  <p className="text-zinc-300 pt-1.5">{step}</p>
                </div>
              ))}
            </div>
            <Button data-testid="how-cta" onClick={() => navigate("/login")} className="mt-9 bg-purple-600 hover:bg-purple-500 text-white rounded-full px-7 py-6">
              Crear mi cuenta gratis <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="precios" className="border-y border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-zinc-500 mb-4">Paquetes mensuales</p>
            <h2 className="font-display font-black tracking-tight text-3xl sm:text-5xl">
              Elige el paquete de tu <span className="text-yellow-impact">crecimiento</span>
            </h2>
            <p className="text-zinc-400 mt-4">Todo el poder de la IA para hacer crecer tu red, desde el primer día.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 items-stretch">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                data-testid={`package-${pkg.id}`}
                className={`relative rounded-2xl border p-7 flex flex-col w-full max-w-md ${pkg.popular ? "border-yellow-400 bg-yellow-400/5 glow-purple" : "border-white/10 bg-[#121212]"}`}
              >
                {pkg.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Más popular</span>
                )}
                <h3 className="font-display font-bold text-2xl">{pkg.name}</h3>
                <p className="text-sm text-zinc-400 mt-1">{pkg.tagline}</p>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-display font-black text-5xl">${pkg.price}</span>
                  <span className="text-zinc-500 mb-1.5">{pkg.currency}/{pkg.period}</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <Check size={16} className={`mt-0.5 shrink-0 ${pkg.popular ? "text-yellow-400" : "text-purple-500"}`} /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`choose-${pkg.id}`}
                  onClick={() => navigate("/dashboard/planes")}
                  className={`mt-7 w-full rounded-xl py-6 font-bold ${pkg.popular ? "bg-yellow-400 hover:bg-yellow-300 text-black" : "bg-purple-600 hover:bg-purple-500 text-white"}`}
                >
                  Elegir {pkg.name}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28 text-center">
        <Crown className="mx-auto text-yellow-400 mb-6" size={40} />
        <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl max-w-4xl mx-auto leading-tight">
          El líder que <span className="text-purple-500">mereces ser</span> empieza con un solo clic
        </h2>
        <Button data-testid="final-cta" onClick={() => navigate("/login")} className="mt-9 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-10 py-7 text-base">
          Comenzar ahora <ArrowRight size={18} className="ml-2" />
        </Button>
      </section>

      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row justify-between gap-8">
            <div className="max-w-sm">
              <div className="text-xl mb-3"><Brand /></div>
              <p className="text-sm text-zinc-500">MARKETING DE INTELIGENCIA ARTIFICIAL S de RL de CV</p>
              <p className="text-sm text-zinc-600 mt-1">Av. Américas 1545, pisos 20, 21 y 22, Guadalajara, Jalisco, México.</p>
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-600 font-semibold">Información</span>
              <Link data-testid="footer-quienes" to="/quienes-somos" className="text-sm text-zinc-300 hover:text-yellow-impact transition-colors">Quiénes Somos</Link>
              <Link data-testid="footer-privacidad" to="/aviso-privacidad" className="text-sm text-zinc-300 hover:text-yellow-impact transition-colors">Aviso de Privacidad</Link>
              <Link data-testid="footer-eliminacion" to="/eliminacion-datos" className="text-sm text-zinc-300 hover:text-yellow-impact transition-colors">Eliminación de Datos del Usuario</Link>
              <Link data-testid="footer-terminos" to="/terminos" className="text-sm text-zinc-300 hover:text-yellow-impact transition-colors">Términos del Servicio</Link>
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6 text-center">
            <p className="text-sm text-zinc-600">© {new Date().getFullYear()} MULTICONTACTOS · Herramientas para líderes de multinivel</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
